import { GBToBytes } from "@sscs/utils";
import {
  GroupRole,
  OrgRole,
  PORTAL_GROUP_ROLES,
  PORTAL_ORG_ROLES,
  TRIAL_GROUP_ROLES,
  TRIAL_ORG_ROLES,
} from "Types";
import { generateEmailSchema } from "Utils";
import * as yup from "Utils/yup";
import {
  CreateGroupRequest,
  GroupFormData,
  GroupFormPercentageSlider,
  MemberFormData,
  MembersFormData,
  UpdateGroupRequest,
} from "./types";

export const generateMemberSchema = (): yup.SchemaOf<MemberFormData> =>
  yup.object({
    email: generateEmailSchema(),
    orgRole: yup
      .mixed<OrgRole>()
      .oneOf(
        [...PORTAL_ORG_ROLES, ...TRIAL_ORG_ROLES],
        "Organization role is not recognized",
      ),
    orgGroups: yup.array(yup.string().required()).optional(),
  });

export const generateMembersSchema = (): yup.SchemaOf<MembersFormData> =>
  yup.object({
    emails: yup
      .array(generateEmailSchema())
      .min(1, "At least ${min} email address is required")
      .max(100, "${max} maximum email addresses allowed"),
    orgRole: yup
      .mixed<OrgRole>()
      .oneOf(
        [...PORTAL_ORG_ROLES, ...TRIAL_ORG_ROLES],
        "Organization role is not recognized",
      ),
    groupRole: yup
      .mixed<GroupRole>()
      .oneOf(
        [...PORTAL_GROUP_ROLES, ...TRIAL_GROUP_ROLES],
        "Group role is not recognized",
      ),
    orgGroups: yup.array(yup.string().required()).optional(),
  });

export const generateGroupFormSchema = (
  percentageSliderSchema: yup.SchemaOf<GroupFormPercentageSlider>,
  totalPortalCapacity = 0,
): yup.SchemaOf<GroupFormData> =>
  percentageSliderSchema.concat(
    yup.object().shape({
      name: yup
        .string()
        .max(200, "Max group name length is 200 characters")
        .required("Group name is required")
        .purlSafe(),
      description: yup
        .mixed()
        .nullable(true)
        .test(
          "max-chars",
          "Max group description length is 500 characters",
          (description) => description.length <= 500,
        ),
      is_default: yup.boolean().required(),
      oidc_sso_group_mappings: yup
        .array()
        .test(
          "mappings",
          "Max group name length is 500 characters",
          (groupNames) =>
            !groupNames?.some((groupName) => groupName.length > 500),
        )
        .optional(),
      limited: yup
        .number()
        .required()
        .min(0, "Value must be greater than or equal to 0")
        .max(
          totalPortalCapacity,
          `Monthly limit can not exceed total Portal capacity: ${totalPortalCapacity.toFixed(
            2,
          )}`,
        )
        .typeError("Please enter a valid number"),
    }),
  );

const getGroupFormBodyBase = (
  data: GroupFormData,
  organizationId: string,
): Omit<CreateGroupRequest["body"], "limit"> => ({
  name: data.name,
  description: data?.description ?? "",
  is_default: data.is_default,
  organization_id: organizationId ?? "",
  oidc_sso_group_mappings: data?.oidc_sso_group_mappings ?? [],
});

export const getCreateGroupFormBody = (
  data: GroupFormData,
  organizationId: string,
): CreateGroupRequest["body"] => {
  const createGroupFormBody: CreateGroupRequest["body"] = getGroupFormBodyBase(
    data,
    organizationId,
  );

  if (data.limited > 0 && data.filestream_percentage !== null) {
    createGroupFormBody.limit = {
      projects_percentage: 100 - data.filestream_percentage,
      limit_bytes: GBToBytes(data.limited),
    };
  }

  return createGroupFormBody;
};

export const getEditGroupFormBody = (
  data: GroupFormData,
  organizationId: string,
): UpdateGroupRequest["body"] => {
  const editGroupFormBody: UpdateGroupRequest["body"] = getGroupFormBodyBase(
    data,
    organizationId,
  );

  if (data.limited > 0 && data.filestream_percentage !== null) {
    editGroupFormBody.limit = {
      limit_bytes: GBToBytes(data.limited),
      projects_percentage: 100 - data.filestream_percentage,
    };
  } else {
    editGroupFormBody.limit = {};
  }

  return editGroupFormBody;
};
